import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, Chip, CircularProgress, Tooltip } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";

// Detailed explanation of how "Suggest defaults" ranks chapters (shown on hover).
const SUGGEST_HELP = (
    <Box sx={{ maxWidth: 330, p: 0.5 }}>
        <Typography variant="subtitle2" gutterBottom>
            How “Suggest defaults” ranks chapters
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
            Each chapter gets a priority score of{" "}
            <b>cohort miss-rate&nbsp;×&nbsp;SAT&nbsp;blueprint&nbsp;weight</b> — so chapters that are
            both <i>harder</i> and <i>more heavily tested</i> rank first.
        </Typography>
        <Typography variant="caption" component="div" sx={{ mb: 0.75 }}>
            <b>Cohort miss-rate</b> — how often students get this chapter’s questions wrong, pooled
            across <i>everyone’s</i> q-bank practice and practice-test attempts. Chapters with too few
            attempts fall back to the cohort average, so they’re ordered by blueprint weight alone.
        </Typography>
        <Typography variant="caption" component="div" sx={{ mb: 0.75 }}>
            <b>SAT blueprint weight</b> — how heavily the official Digital&nbsp;SAT weights this
            chapter’s content domain (Math: Algebra &amp; Advanced&nbsp;Math 35% each, Problem-Solving/Data
            &amp; Geometry/Trig 15% each; R&amp;W: Craft&nbsp;&amp;&nbsp;Structure 28%, Information&nbsp;&amp;&nbsp;Ideas
            and Conventions 26%, Expression of Ideas 20%).
        </Typography>
        <Typography variant="caption" component="div" sx={{ fontStyle: "italic" }}>
            It’s only a starting point — drag to fine-tune the order, then Save.
        </Typography>
    </Box>
);

// Generic version of the "Suggest defaults" help for non-DSAT programs (ACT/AP), where
// there is no content-domain blueprint — ranking is by cohort miss-rate alone.
const SUGGEST_HELP_GENERIC = (
    <Box sx={{ maxWidth: 330, p: 0.5 }}>
        <Typography variant="subtitle2" gutterBottom>
            How “Suggest defaults” ranks chapters
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
            Chapters are ranked by <b>cohort miss-rate</b> — the ones students get wrong most often
            rank first.
        </Typography>
        <Typography variant="caption" component="div" sx={{ mb: 0.75 }}>
            <b>Cohort miss-rate</b> — how often students get this chapter’s questions wrong, pooled
            across everyone’s q-bank practice and practice-test attempts. (The detailed content-domain
            weighting is tuned for the Digital&nbsp;SAT; for other tests, ranking leans primarily on miss-rate.)
        </Typography>
        <Typography variant="caption" component="div" sx={{ fontStyle: "italic" }}>
            It’s only a starting point — drag to fine-tune the order, then Save.
        </Typography>
    </Box>
);

// Drag-to-order fallback chapter priorities for one SUBJECT program-course.
// Practiceable chapters (question_count>0) come from the ETT taxonomy; the order
// is persisted as priority via PUT /chapter-priorities/:programCourseId.
const ChapterPriorityEditor = ({ programUuid, programCourse, qbType }) => {
    const base = `${process.env.REACT_APP_URL}/api/v1/prep-programs`;
    const auth = { headers: { Authorization: getAuthHeader() } };

    const [chapters, setChapters] = useState([]); // ordered [{chapter_id, title}]
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [suggesting, setSuggesting] = useState(false);
    const [savedMsg, setSavedMsg] = useState("");

    const load = useCallback(async () => {
        if (!programCourse?.ett_qb_subject_id || !qbType) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const [taxRes, priRes] = await Promise.all([
                Axios.get(`${base}/taxonomy`, { ...auth, params: { qb_type: qbType } }),
                Axios.get(`${base}/${programUuid}/chapter-priorities/${programCourse.id}`, auth),
            ]);
            const subject = (taxRes.data || []).find((s) => s.subject_id === programCourse.ett_qb_subject_id);
            const practiceable = (subject?.chapters || []).filter((c) => c.question_count > 0);
            const byId = new Map(practiceable.map((c) => [c.id, c]));
            const ordered = [];
            (priRes.data || []).forEach((p) => {
                if (byId.has(p.chapter_id)) {
                    ordered.push(byId.get(p.chapter_id));
                    byId.delete(p.chapter_id);
                }
            });
            byId.forEach((c) => ordered.push(c)); // remaining practiceable chapters
            setChapters(ordered.map((c) => ({ chapter_id: c.id, title: c.title })));
        } catch (e) {
            console.error("Load chapter priorities error:", e);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programUuid, programCourse?.id, programCourse?.ett_qb_subject_id, qbType]);

    useEffect(() => {
        load();
    }, [load]);

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(chapters);
        const [moved] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, moved);
        setChapters(items);
    };

    // Reorder the current chapters by the server's blended suggestion (cohort miss-rate
    // × SAT blueprint weight). We reorder rather than replace, so the editor's chapter
    // set stays authoritative; chapters absent from the suggestion keep their relative
    // order at the end. Nothing persists until the admin reviews and Saves.
    const suggest = async () => {
        setSuggesting(true);
        setSavedMsg("");
        try {
            const { data } = await Axios.get(
                `${base}/${programUuid}/chapter-priorities/${programCourse.id}/suggest`,
                auth
            );
            const rank = new Map((data || []).map((c, i) => [c.chapter_id, i]));
            const reordered = [...chapters].sort(
                (a, b) =>
                    (rank.has(a.chapter_id) ? rank.get(a.chapter_id) : Infinity) -
                    (rank.has(b.chapter_id) ? rank.get(b.chapter_id) : Infinity)
            );
            setChapters(reordered);
            setSavedMsg("Suggested order applied — review and Save");
        } catch (e) {
            console.error("Suggest chapter priorities error:", e);
            setSavedMsg("Could not load suggestions");
        } finally {
            setSuggesting(false);
        }
    };

    const save = async () => {
        setSaving(true);
        setSavedMsg("");
        try {
            await Axios.put(
                `${base}/${programUuid}/chapter-priorities/${programCourse.id}`,
                { chapters: chapters.map((c, i) => ({ chapter_id: c.chapter_id, priority: i })) },
                auth
            );
            setSavedMsg("Saved");
        } catch (e) {
            console.error("Save chapter priorities error:", e);
            setSavedMsg("Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <CircularProgress size={20} />;
    if (!chapters.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                No practiceable chapters for this subject.
            </Typography>
        );
    }

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 1 }}>
                <Tooltip title={["dsat", "sat"].includes(String(qbType || "").toLowerCase()) ? SUGGEST_HELP : SUGGEST_HELP_GENERIC} arrow>
                    <span>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={suggest}
                            disabled={suggesting || saving}
                            startIcon={suggesting ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon fontSize="small" />}
                            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                        >
                            Suggest defaults
                        </Button>
                    </span>
                </Tooltip>
                <Typography variant="body2" color="text.secondary">
                    Drag to set fallback priority (used when a student has no recent practice data).
                </Typography>
            </Box>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="chapter-priorities">
                    {(provided) => (
                        <Box ref={provided.innerRef} {...provided.droppableProps}>
                            {chapters.map((c, index) => (
                                <Draggable key={c.chapter_id} draggableId={String(c.chapter_id)} index={index}>
                                    {(p) => (
                                        <Box
                                            ref={p.innerRef}
                                            {...p.draggableProps}
                                            {...p.dragHandleProps}
                                            sx={{
                                                display: "flex", alignItems: "center", gap: 1, p: 1, mb: 0.5,
                                                border: "1px solid", borderColor: "divider", borderRadius: 1,
                                                bgcolor: "background.paper",
                                            }}
                                        >
                                            <DragIndicatorIcon fontSize="small" color="disabled" />
                                            <Chip size="small" label={index + 1} />
                                            <Typography variant="body2">{c.title}</Typography>
                                        </Box>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>
            <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
                <Button variant="outlined" size="small" onClick={save} disabled={saving || suggesting}>
                    Save priorities
                </Button>
                {savedMsg && <Typography variant="caption" color="text.secondary">{savedMsg}</Typography>}
            </Box>
        </Box>
    );
};

export default ChapterPriorityEditor;
